import React from "react";
import { ChartCard } from "@/components/ChartCard";
import DataUploadCard from "@/components/DataUploadCard";
import { useDashboardData } from "@/hooks/useDashboardData";
import { formatTRY, formatNumber } from "@/lib/api";
import { Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const statusBadge = {
  "Teslim Edildi": "bg-emerald-50 text-emerald-700",
  "Yolda": "bg-[#E5F0FA] text-[#0054E3]",
  "Gecikmeli": "bg-amber-50 text-amber-700",
  "İptal": "bg-rose-50 text-rose-700",
};

export default function Reports() {
  const { data, loading } = useDashboardData("/dashboard/shipments", { limit: 100 });
  const rows = data || [];

  const exportCsv = () => {
    if (!rows.length) return;
    const headers = ["TM ID", "Tarih", "Kaynak Lokasyon", "Nakliyeci", "Mod", "Ağırlık (ton)", "Maliyet (TRY)", "Durum"];
    const csv = [
      headers.join(","),
      ...rows.map((r) => [
        r.id,
        r.date.slice(0, 10),
        `"${r.source_name}"`,
        `"${r.carrier_name}"`,
        r.mode,
        r.weight_tons,
        r.cost_try,
        r.status,
      ].join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sap-tm-shipments-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV indirildi");
  };

  const exportXlsx = () => {
    if (!rows.length) return;
    const data = rows.map((r) => ({
      "TM ID": r.id,
      "Tarih": r.date.slice(0, 10),
      "Kaynak Lokasyon": r.source_name,
      "Nakliyeci": r.carrier_name,
      "Mod": r.mode,
      "Ağırlık (ton)": r.weight_tons,
      "Maliyet (TRY)": r.cost_try,
      "Zamanında": r.on_time ? "Evet" : "Hayır",
      "Durum": r.status,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [
      { wch: 12 }, { wch: 12 }, { wch: 22 }, { wch: 20 }, { wch: 12 },
      { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 16 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sevkiyatlar");
    XLSX.writeFile(wb, `sap-tm-sevkiyatlar-${Date.now()}.xlsx`);
    toast.success("Excel dosyası indirildi");
  };

  return (
    <div className="space-y-6">
      <DataUploadCard />

      <ChartCard
        testid="chart-shipments-table"
        title="Son Sevkiyatlar"
        subtitle="Filtrelere göre detaylı kayıt listesi"
        action={
          <div className="flex items-center gap-2">
            <Button data-testid="export-xlsx" onClick={exportXlsx} size="sm" className="bg-[#0070F2] hover:bg-[#0054E3] text-white">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
            </Button>
            <Button data-testid="export-csv" onClick={exportCsv} size="sm" variant="outline" className="border-slate-300">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
          </div>
        }
      >
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold bg-slate-50">
                <th className="p-3 pl-5">TM ID</th>
                <th className="p-3">Tarih</th>
                <th className="p-3">Kaynak Lokasyon</th>
                <th className="p-3">Nakliyeci</th>
                <th className="p-3">Mod</th>
                <th className="p-3 text-right">Ağırlık</th>
                <th className="p-3 text-right">Maliyet</th>
                <th className="p-3 pr-5">Durum</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-500">Yükleniyor…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-500">Kayıt bulunamadı.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.id} data-testid={`shipment-row-${i}`} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                  <td className="p-3 pl-5 font-mono text-xs text-slate-700">{r.id}</td>
                  <td className="p-3 text-slate-600">{new Date(r.date).toLocaleDateString("tr-TR")}</td>
                  <td className="p-3 text-slate-800 font-medium">{r.source_name}</td>
                  <td className="p-3 text-slate-700">{r.carrier_name}</td>
                  <td className="p-3 text-slate-600">{r.mode}</td>
                  <td className="p-3 text-right text-slate-700 tabular-nums">{formatNumber(r.weight_tons)} t</td>
                  <td className="p-3 text-right font-semibold text-slate-900 tabular-nums">{formatTRY(r.cost_try)}</td>
                  <td className="p-3 pr-5">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${statusBadge[r.status] || "bg-slate-100 text-slate-600"}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}
